import { useQuery } from "@tanstack/react-query";
import { contractsApi } from "@/api/contracts.api";
import { testsApi } from "@/api/tests.api";
import { usePayoutReviewQueue } from "@/features/moderator-payout-review/use-payout-review";
import { ContractStatus } from "@/lib/types/contracts";
import { countContractsToValidate, type TodoCounts } from "./moderator-todo";

const todoKeys = {
  tests: ["moderator", "todo", "tests"] as const,
  drafts: ["moderator", "todo", "contracts-to-send"] as const,
  contracts: ["moderator", "todo", "contracts"] as const,
};

/**
 * The counters of the to-do page and of the menu badge. Same endpoints the
 * work pages call; react-query shares them between the menu and the page.
 */
export function useModeratorTodoCounts(): TodoCounts {
  const tests = useQuery({
    queryKey: todoKeys.tests,
    queryFn: async () => (await testsApi.getPendingSubmissions(1, 10)).total,
    staleTime: 60_000,
  });
  const drafts = useQuery({
    queryKey: todoKeys.drafts,
    queryFn: async () => (await testsApi.getSubmissionsReadyForFinalValidation(1, 10, ContractStatus.DRAFT)).total,
    staleTime: 60_000,
  });
  const contracts = useQuery({
    queryKey: todoKeys.contracts,
    queryFn: async () => countContractsToValidate(await contractsApi.getMyContracts()),
    staleTime: 60_000,
  });
  const payouts = usePayoutReviewQueue({ status: "PENDING", page: 1 });

  return {
    tests: tests.data ?? null,
    contractsToSend: drafts.data ?? null,
    contractsToValidate: contracts.data ?? null,
    payouts: payouts.data?.pagination.total ?? null,
  };
}
